import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useCart } from '../context/CartContext';
import { buildSteps, platillosDelCombo, opcionesDelCombo } from '../utils/combos';
import { parsePresentations } from '../utils/presentations';
import { loadMenuDay, formatDriveImage } from '../utils/menu';
import { loadBusinessHours } from '../utils/businessHours';

// Los platillos del día los sirve utils/menu.loadMenuDay (caché compartido).

// Porción price/qty/unit for an item (falls back to base price when the
// item has no presentations defined — most complements work this way).
const porcionInfo = (item) => {
  const list = parsePresentations({
    presentations: item.presentations,
    unit_of_measure: item.unit_of_measure,
  });
  const porcion = list.find((p) => p.isComboUnit);
  if (porcion) return { price: porcion.price, qty: porcion.qty, unit: porcion.unit };
  return { price: parseFloat(item.price) || 0, qty: 1, unit: item.unit_of_measure || '' };
};

// Guided combo builder. Porción is locked for every pick. Items are added to
// the cart as a combo group carrying the combo's fixed price. When
// editingGroupId is set, the existing group is replaced (edit mode).
const ComboWizard = ({ isOpen, combo, combos = [], onClose, editingGroupId = null, initialSelections = null, noDiscount = false }) => {
  const { addComboGroup } = useCart();
  const [itemsByDay, setItemsByDay] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [selections, setSelections] = useState([]);
  // Al editar un solo paso desde el resumen, tras elegir se regresa directo
  // al resumen en vez de continuar el flujo lineal.
  const [editingFromSummary, setEditingFromSummary] = useState(false);

  const steps = useMemo(() => (combo ? buildSteps(combo.categories) : []), [combo]);

  // Fetch the day's items when the wizard opens.
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setLoading(true);
    setStepIndex(0);
    const fetchItems = async () => {
      try {
        const businessHours = await loadBusinessHours();
        const menuDay = await loadMenuDay(businessHours);
        if (active) setItemsByDay(menuDay.items);
      } catch (error) {
        console.error('Error al obtener platillos del combo:', error);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchItems();
    return () => { active = false; };
  }, [isOpen]);

  // Initialise selections (empty, or preloaded from the group when editing).
  useEffect(() => {
    if (!isOpen || steps.length === 0) return;
    if (initialSelections && itemsByDay.length > 0) {
      const next = steps.map((_, i) => {
        const target = initialSelections[i];
        if (!target) return null;
        const item = itemsByDay.find((it) => it.id === target.id) || null;
        // Lo precargado también respeta la lista del combo (allowed_items): un
        // refresco no entra al "Combo con agua", ese paso queda por elegir.
        if (item && !noDiscount && steps[i].cat !== 1
          && opcionesDelCombo(combo, steps[i].cat, [item]).length === 0) return null;
        return item;
      });
      setSelections(next);
      // Una selección = un paso. El item sembrado/precargado pre-llena su paso
      // (queda marcado y cuenta como paso), pero NO se aterriza en él: se arranca
      // en el primer paso que falte para no obligar a re-marcarlo. En modo
      // edición arrancamos en el resumen para que elija qué cambiar.
      setEditingFromSummary(false);
      if (editingGroupId) {
        setStepIndex(steps.length);
      } else {
        const firstEmpty = next.findIndex((s) => !s);
        setStepIndex(firstEmpty === -1 ? steps.length : firstEmpty);
      }
    } else {
      setSelections(new Array(steps.length).fill(null));
      setEditingFromSummary(false);
      setStepIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, steps, itemsByDay]);

  if (!isOpen || !combo) return null;

  const isSummary = stepIndex >= steps.length;
  const currentStep = steps[stepIndex];

  const optionsForStep = (step) => {
    if (!step) return [];
    const opciones = itemsByDay.filter(
      (it) =>
        it.category_id === step.cat &&
        it.apply_promotions === 1 &&
        (it.product_counter || 0) > 0
    );
    // Platillo: solo los que admite este combo (main_items); el resto, los de
    // su lista (allowed_items). Sin descuento es un plato a precio lleno, ahí
    // no aplica.
    if (noDiscount) return opciones;
    return step.cat === 1
      ? platillosDelCombo(combo, combos, opciones)
      : opcionesDelCombo(combo, step.cat, opciones);
  };

  const toSummary = () => {
    setEditingFromSummary(false);
    setStepIndex(steps.length);
  };

  // Siguiente paso que aún falta (salta los ya marcados/sembrados); si no falta
  // ninguno, va al resumen.
  const nextMissing = (from, sel) => {
    let i = from + 1;
    while (i < steps.length && sel[i]) i += 1;
    return i;
  };

  const selectItem = (item) => {
    const updated = selections.map((s, i) => (i === stepIndex ? item : s));
    setSelections(updated);
    // Editando desde el resumen: una elección basta y regresa al resumen.
    if (editingFromSummary) toSummary();
    else setStepIndex(nextMissing(stepIndex, updated));
  };

  const goBack = () => {
    if (editingFromSummary) toSummary();
    else setStepIndex((idx) => Math.max(idx - 1, 0));
  };
  const goNext = () => {
    if (editingFromSummary) toSummary();
    else setStepIndex(nextMissing(stepIndex, selections));
  };

  // Editar un paso puntual desde el resumen.
  const editStep = (i) => {
    setEditingFromSummary(true);
    setStepIndex(i);
  };

  const individualSum = selections.reduce((s, it) => s + (it ? porcionInfo(it).price : 0), 0);
  const savings = Math.max(0, individualSum - combo.price);
  const allChosen = selections.length === steps.length && selections.every(Boolean);

  const handleConfirm = () => {
    if (!allChosen) return;
    const chosenItems = selections.map((it) => {
      const info = porcionInfo(it);
      return {
        id: it.id,
        title: it.Nombre || it.name || 'Platillo',
        image: formatDriveImage(it.img_item),
        categoryId: it.category_id,
        applyPromotions: it.apply_promotions,
        presentation: 'porcion',
        presentationLabel: 'Porción',
        price: info.price,
        basePrice: it.price,
        qty: info.qty,
        unit: info.unit,
        availability: it.product_counter || 0,
        // El combo siempre se arma con porciones, la unidad en la que se lleva
        // el contador: cada pieza consume exactamente 1.
        unitsPerItem: 1,
      };
    });
    // Sin descuento: el grupo se nombra con el platillo (cat 1) y se cobra a
    // precio lleno (suma de porciones), no con el nombre/precio fijo del combo.
    const platillo = chosenItems.find((it) => it.categoryId === 1) || chosenItems[0];
    addComboGroup(combo, chosenItems, editingGroupId, {
      noDiscount,
      displayName: noDiscount ? (platillo?.title || 'Tu plato') : combo.name,
    });
    onClose();
  };

  // Botón secundario del footer (paso normal): Cancelar en paso 0 o editando,
  // Atrás en pasos intermedios.
  const secondaryLabel = editingFromSummary ? 'Cancelar' : (stepIndex === 0 ? 'Cancelar' : 'Atrás');
  const secondaryAction = (editingFromSummary || stepIndex !== 0) ? goBack : onClose;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="relative bg-gradient-to-br from-red-900/95 via-red-800/95 to-red-950/95 backdrop-blur-sm rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-amber-400/30 max-h-[90vh] overflow-y-auto custom-scrollbar"
            initial={{ scale: 0.9, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 40 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-all duration-200 z-10 p-2 hover:bg-white/10 rounded-full"
              aria-label="Cerrar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="relative p-6">
              {/* Header */}
              <div className="mb-5">
                <p className="text-amber-300 text-xs font-semibold uppercase tracking-wider mb-1">
                  {noDiscount
                    ? (editingGroupId ? 'Editar plato' : 'Arma tu plato')
                    : (editingGroupId ? 'Editar combo' : 'Arma tu combo')}
                </p>
                <h2 className="text-white text-xl font-bold leading-tight">{combo.name}</h2>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-white/70 text-sm">{combo.composition}</span>
                  {noDiscount ? (
                    <span className="text-white/50 text-xs">Suma de porciones</span>
                  ) : (
                    <span className="text-amber-300 font-black text-lg">${combo.price}</span>
                  )}
                </div>

                {/* Progress */}
                <div className="mt-4">
                  <div className="flex justify-between text-[11px] text-white/60 mb-1.5">
                    <span>{isSummary ? 'Resumen' : currentStep?.title}</span>
                    <span>{isSummary ? `${steps.length} de ${steps.length}` : `Paso ${stepIndex + 1} de ${steps.length}`}</span>
                  </div>
                  {/* Una barrita por paso; vacía hasta elegir, llena al 100% al seleccionar */}
                  <div className="flex gap-1.5">
                    {steps.map((_, i) => {
                      const done = isSummary || !!selections[i];
                      return (
                        <div key={i} className="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden">
                          <motion.div
                            className="h-full rounded-full bg-amber-400"
                            initial={false}
                            animate={{ width: done ? '100%' : '0%' }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Body */}
              {loading ? (
                <div className="py-12 text-center text-white/70 text-sm">Cargando platillos…</div>
              ) : isSummary ? (
                <div className="space-y-2">
                  {steps.map((step, i) => {
                    const it = selections[i];
                    const info = it ? porcionInfo(it) : null;
                    return (
                      <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg p-2.5">
                        <div className="min-w-0 pr-2">
                          <p className="text-white/50 text-[10px] uppercase tracking-wider">{step.title}</p>
                          <p className="text-white text-sm truncate">{it ? (it.Nombre || it.name) : '—'}</p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {info && <span className="text-white/50 text-[11px]">${info.price}</span>}
                          <button
                            onClick={() => editStep(i)}
                            className="text-amber-300 hover:text-amber-200 text-xs font-medium transition-colors cursor-pointer"
                          >
                            Cambiar
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  <div className="mt-3 pt-3 border-t border-white/15">
                    {noDiscount ? (
                      <div className="flex justify-between items-center">
                        <span className="text-white text-sm font-semibold">Total</span>
                        <span className="text-white font-black text-lg">${individualSum}</span>
                      </div>
                    ) : (
                      <>
                        {savings > 0 && (
                          <div className="flex justify-between text-white/50 text-xs">
                            <span>Por separado</span>
                            <span className="line-through">${individualSum}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-amber-300 text-sm font-semibold">Precio combo</span>
                          <span className="text-white font-black text-lg">${combo.price}</span>
                        </div>
                        {savings > 0 && (
                          <p className="text-green-300 text-xs mt-1 flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Ahorras ${savings}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {optionsForStep(currentStep).length === 0 ? (
                    <p className="py-10 text-center text-white/70 text-sm">
                      No hay opciones de {currentStep?.label?.toLowerCase()} disponibles hoy.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {optionsForStep(currentStep).map((it) => {
                        const sel = selections[stepIndex] && selections[stepIndex].id === it.id;
                        const info = porcionInfo(it);
                        const img = formatDriveImage(it.img_item);
                        return (
                          <button
                            key={it.id}
                            type="button"
                            onClick={() => selectItem(it)}
                            aria-pressed={sel}
                            className={`text-left rounded-xl overflow-hidden border transition-colors duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-300/70 ${sel
                              ? 'border-amber-400 bg-white/15'
                              : 'border-white/15 bg-white/5 hover:bg-white/10'
                              }`}
                          >
                            <div className="aspect-square bg-white/5 relative">
                              {img ? (
                                <img src={img} alt={it.Nombre || it.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <svg className="w-8 h-8 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              )}
                              {sel && (
                                <div className="absolute top-1.5 right-1.5 w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center">
                                  <svg className="w-4 h-4 text-amber-950" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div className="p-2">
                              <p className="text-white text-xs font-medium leading-tight line-clamp-2">{it.Nombre || it.name}</p>
                              <p className="text-white/60 text-[11px] mt-0.5">Porción · ${info.price}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Footer */}
              <div className="grid grid-cols-2 gap-3 mt-6">
                {isSummary ? (
                  <>
                    <button
                      onClick={handleConfirm}
                      disabled={!allChosen}
                      className="h-11 rounded-xl bg-amber-400 text-amber-950 font-bold text-sm flex items-center justify-center gap-2 cursor-pointer transition-colors duration-200 hover:bg-amber-300 disabled:bg-white/20 disabled:text-white/40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-amber-300/70 shadow-lg"
                    >
                      {editingGroupId ? 'Guardar cambios' : 'Agregar al carrito'}
                    </button>
                    <button
                      onClick={goBack}
                      className="h-11 rounded-xl text-white font-semibold text-sm hover:bg-white/10 transition-colors duration-200 cursor-pointer"
                    >
                      Regresar
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={goNext}
                      disabled={!selections[stepIndex]}
                      className="h-11 rounded-xl bg-amber-400 text-amber-950 font-bold text-sm flex items-center justify-center gap-2 cursor-pointer transition-colors duration-200 hover:bg-amber-300 disabled:bg-white/20 disabled:text-white/40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-amber-300/70 shadow-lg"
                    >
                      {editingFromSummary ? 'Listo' : (stepIndex === steps.length - 1 ? 'Ver resumen' : 'Siguiente')}
                    </button>
                    <button
                      onClick={secondaryAction}
                      className="h-11 rounded-xl text-white font-semibold text-sm hover:bg-white/10 transition-colors duration-200 cursor-pointer"
                    >
                      {secondaryLabel}
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ComboWizard;
